import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { FlaskConical, Loader2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useCreateSimulationBatch, useEvalDatasets, useRunSimulationBatch } from '@/hooks/useEvaluator';
import { useToast } from '@/hooks/use-toast';
import type { PromptVersion } from '@/lib/api/promptVersions';
import { cn } from '@/lib/utils';

interface RunSimulationSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string | undefined;
  promptName: string | null | undefined;
  versions: PromptVersion[];
  requestedVersion: number | null;
  onSimulationCreated: (batchId: string) => void;
  onCreatePopulation: () => void;
}

function versionLabel(version: PromptVersion) {
  return `Version ${version.version}`;
}

function statusLabel(status: PromptVersion['status']) {
  if (status === 'live') return 'Live';
  if (status === 'staging') return 'Staging';
  return 'Draft';
}

function statusClassName(status: PromptVersion['status']) {
  if (status === 'live') return 'bg-success/15 text-success border-success/20';
  if (status === 'staging') return 'bg-primary/10 text-foreground border-primary/15';
  return 'bg-muted text-muted-foreground border-border';
}

function defaultVersion(versions: PromptVersion[], requestedVersion: number | null) {
  const requested = requestedVersion ? versions.find((version) => version.version === requestedVersion) : null;
  return requested
    ?? versions.find((version) => version.status === 'staging')
    ?? versions.find((version) => version.status === 'live')
    ?? versions[0]
    ?? null;
}

function formatDateForName(date: Date) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function RunSimulationSheet({
  open,
  onOpenChange,
  organizationId,
  promptName,
  versions,
  requestedVersion,
  onSimulationCreated,
  onCreatePopulation,
}: RunSimulationSheetProps) {
  const { toast } = useToast();
  const { data: datasetsData, isLoading: datasetsLoading } = useEvalDatasets(organizationId);
  const createSimulationBatch = useCreateSimulationBatch();
  const runSimulationBatch = useRunSimulationBatch();
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [selectedPopulations, setSelectedPopulations] = useState<string[]>([]);

  const datasets = useMemo(
    () => [...(datasetsData?.datasets ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [datasetsData?.datasets],
  );

  const eligibleDatasets = useMemo(
    () => datasets.filter((dataset) => (dataset.item_count ?? 0) > 0),
    [datasets],
  );

  useEffect(() => {
    if (!open) return;
    const nextDefault = defaultVersion(versions, requestedVersion);
    setSelectedVersion(nextDefault ? String(nextDefault.version) : '');
    setSelectedPopulations([]);
  }, [open, requestedVersion, versions]);

  const selectedVersionNumber = selectedVersion ? Number(selectedVersion) : null;
  const selectedVersionDetail = versions.find((version) => version.version === selectedVersionNumber) ?? null;
  const isSubmitting = createSimulationBatch.isPending || runSimulationBatch.isPending;
  const canSubmit = Boolean(organizationId && promptName && selectedVersionNumber && selectedPopulations.length > 0);

  const togglePopulation = (name: string, checked: boolean) => {
    setSelectedPopulations((current) => (
      checked ? Array.from(new Set([...current, name])) : current.filter((item) => item !== name)
    ));
  };

  const handleSubmit = async () => {
    if (!organizationId || !promptName || !selectedVersionNumber || selectedPopulations.length === 0) return;

    try {
      const batch = await createSimulationBatch.mutateAsync({
        name: `Simulation ${formatDateForName(new Date())}`,
        organization_id: organizationId,
        prompt_name: promptName,
        prompt_version: selectedVersionNumber,
        dataset_names: selectedPopulations,
      });

      onSimulationCreated(batch.id);

      runSimulationBatch.mutate(
        {
          batchId: batch.id,
          data: {
            organization_id: organizationId,
            prompt_version: selectedVersionNumber,
          },
        },
        {
          onError: (error) => {
            toast({
              title: 'Simulation could not start',
              description: error instanceof Error ? error.message : 'Try again from History.',
              variant: 'destructive',
            });
          },
        },
      );
    } catch (error) {
      toast({
        title: 'Simulation could not start',
        description: error instanceof Error ? error.message : 'Check the selected version and populations.',
        variant: 'destructive',
      });
    }
  };

  if (!open) return null;

  return (
    <motion.aside
      initial={{ x: 32, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 32, opacity: 0 }}
      transition={{ duration: 0.16, ease: 'easeOut' }}
      className="flex h-full w-[400px] shrink-0 flex-col py-5 pr-4 sm:pr-6 md:pr-8"
    >
      <div className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-background">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <FlaskConical className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h2 className="truncate text-sm font-semibold text-foreground">Run Simulation</h2>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-muted-foreground"
          onClick={() => onOpenChange(false)}
          disabled={isSubmitting}
          aria-label="Close run simulation rail"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-auto">
        <section className="border-b border-border px-4 py-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Agent version
          </p>

          {versions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Create an agent version before running a simulation.
            </p>
          ) : (
            <>
              <Select value={selectedVersion} onValueChange={setSelectedVersion}>
                <SelectTrigger className="h-9 w-full">
                  <SelectValue placeholder="Select version">
                    {selectedVersionDetail && (
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm text-foreground">{versionLabel(selectedVersionDetail)}</span>
                        <Badge
                          variant="outline"
                          className={cn('h-5 px-1.5 text-[11px] font-medium', statusClassName(selectedVersionDetail.status))}
                        >
                          {statusLabel(selectedVersionDetail.status)}
                        </Badge>
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {versions.map((version) => (
                    <SelectItem key={version.version} value={String(version.version)}>
                      <span className="flex items-center gap-2">
                        <span className="text-sm text-foreground">{versionLabel(version)}</span>
                        <Badge
                          variant="outline"
                          className={cn('h-5 px-1.5 text-[11px] font-medium', statusClassName(version.status))}
                        >
                          {statusLabel(version.status)}
                        </Badge>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedVersionDetail?.summary && (
                <p className="mt-2 line-clamp-3 text-xs leading-5 text-muted-foreground">
                  {selectedVersionDetail.summary}
                </p>
              )}
            </>
          )}
        </section>

        <section className="px-4 py-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Populations
            </p>
            {eligibleDatasets.length > 1 && (
              <button
                type="button"
                className="text-xs font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setSelectedPopulations(eligibleDatasets.map((dataset) => dataset.name))}
              >
                Select all
              </button>
            )}
          </div>

          {datasetsLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((item) => <Skeleton key={item} className="h-10 rounded-md" />)}
            </div>
          ) : datasets.length === 0 ? (
            <div className="rounded-md border border-dashed bg-background px-3 py-5 text-center">
              <p className="text-sm font-medium text-foreground">No populations yet</p>
              <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onCreatePopulation}>
                Create Population
              </Button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border border-border bg-background">
              <div className="divide-y divide-border">
                {datasets.map((dataset) => {
                  const checked = selectedPopulations.includes(dataset.name);
                  const count = dataset.item_count ?? 0;
                  const isEmpty = count === 0;
                  return (
                    <label
                      key={dataset.name}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 text-sm transition-colors',
                        isEmpty
                          ? 'cursor-not-allowed opacity-60'
                          : 'cursor-pointer hover:bg-muted/40',
                        checked && !isEmpty && 'bg-muted/40',
                      )}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={isEmpty}
                        onCheckedChange={(value) => togglePopulation(dataset.name, value === true)}
                      />
                      <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                        {dataset.name}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {isEmpty ? 'Empty' : `${count} ${count === 1 ? 'persona' : 'personas'}`}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {!datasetsLoading && datasets.length > 0 && eligibleDatasets.length === 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              Add personas to a population before running a simulation.
            </p>
          )}
        </section>
      </div>

      <div className="shrink-0 border-t border-border px-4 py-3">
        <div className="mb-3 flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <span className="truncate">
            {selectedVersionDetail ? versionLabel(selectedVersionDetail) : 'No version'}
            {selectedVersionDetail && (
              <Badge
                variant="outline"
                className={cn('ml-2 h-4 px-1.5 text-[10px]', statusClassName(selectedVersionDetail.status))}
              >
                {statusLabel(selectedVersionDetail.status)}
              </Badge>
            )}
          </span>
          <span className="shrink-0">
            {selectedPopulations.length
              ? `${selectedPopulations.length} ${selectedPopulations.length === 1 ? 'population' : 'populations'}`
              : 'No populations'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" className="flex-1" onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FlaskConical className="mr-1.5 h-4 w-4" />}
            Run
          </Button>
        </div>
      </div>
      </div>
    </motion.aside>
  );
}
